import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import {
  isInquiryProcessAlias,
  isNegotiationProcessAlias,
  OFFER,
} from '../../transactions/transaction';
import {
  pickCategoryFields,
  getDetailCustomFieldValue,
  isFieldForCategory,
  isFieldForListingType,
} from '../../util/fieldHelpers.js';

import {
  AvatarLarge,
  ReviewRating,
  Heading,
  Modal,
  PrimaryButton,
  SecondaryButton,
  NamedLink,
} from '../../components';

import InquiryForm from './InquiryForm/InquiryForm';
import css from './ListingPage.module.css';

/**
 * Breadcrumbs component rendering Home > Category > Subcategory > Listing Name
 */
export const Breadcrumbs = props => {
  const { publicData, categoryConfiguration, listingTitle } = props;

  if (!categoryConfiguration || !categoryConfiguration.categories) {
    return null;
  }

  const { key: prefix, categories } = categoryConfiguration;
  const hierarchy = [];
  let currentOptions = categories;
  let level = 1;

  while (true) {
    const key = `${prefix}${level}`;
    const value = publicData?.[key];
    if (!value) {
      break;
    }
    const option = currentOptions.find(o => o.id === value);
    if (!option) {
      break;
    }
    hierarchy.push({
      id: option.id,
      label: option.label || option.name || option.id,
    });
    currentOptions = option.subcategories || [];
    level++;
  }

  return (
    <nav className={css.breadcrumbs}>
      <NamedLink name="LandingPage" className={css.breadcrumbLink}>
        <FormattedMessage id="ListingPage.breadcrumbHome" defaultMessage="Home" />
      </NamedLink>
      {hierarchy.map(cat => (
        <React.Fragment key={cat.id}>
          <span className={css.breadcrumbSeparator}>/</span>
          <span className={css.breadcrumbItem}>{cat.label}</span>
        </React.Fragment>
      ))}
      <span className={css.breadcrumbSeparator}>/</span>
      <span className={css.breadcrumbCurrent}>{listingTitle}</span>
    </nav>
  );
};

/**
 * ProductDetailsSection displaying Condition, Category, Material, Dimensions, Brand, Color,
 * and any configured custom fields.
 */
/**
 * Helper to format field values of any custom field type in a clean specifications table.
 */
export const resolveFieldDisplayValue = (config, value, intl) => {
  if (value === undefined || value === null) return null;
  if (!config) {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return String(value);
  }

  const { schemaType, enumOptions } = config;

  if (schemaType === 'enum') {
    const option = enumOptions?.find(o => `${value}` === `${o.option}`);
    return option ? option.label : String(value);
  }

  if (schemaType === 'multi-enum') {
    if (Array.isArray(value)) {
      const labels = value
        .map(val => enumOptions?.find(o => `${val}` === `${o.option}`)?.label)
        .filter(Boolean);
      return labels.length > 0 ? labels.join(', ') : null;
    }
    return String(value);
  }

  if (schemaType === 'boolean') {
    return value
      ? intl.formatMessage({ id: 'ListingPage.detailYes', defaultMessage: 'Yes' })
      : intl.formatMessage({ id: 'ListingPage.detailNo', defaultMessage: 'No' });
  }

  if (schemaType === 'long') {
    return String(value);
  }

  if (schemaType === 'shortText' || schemaType === 'text') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return String(value);
};

/**
 * ProductDetailsSection displaying Condition, Category, Material, Dimensions, Brand, Color,
 * and any other configured custom fields matching listing type and category.
 */
export const ProductDetailsSection = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration, intl } = props;

  if (!categoryConfiguration) {
    return null;
  }

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  // Find category hierarchy labels path
  const getCategoryLabels = () => {
    if (!categoryConfiguration || !categoryConfiguration.categories) return null;
    const { key: prefix, categories } = categoryConfiguration;
    const labels = [];
    let currentOptions = categories;
    let level = 1;
    while (true) {
      const key = `${prefix}${level}`;
      const value = publicData?.[key];
      if (!value) break;
      const option = currentOptions.find(o => o.id === value);
      if (!option) break;
      labels.push(option.name || option.label || value);
      currentOptions = option.subcategories || [];
      level++;
    }
    return labels.length > 0 ? labels.join(' > ') : null;
  };

  const categoryLabelValue = getCategoryLabels();

  const isFieldForSelectedCategories = fieldConfig => {
    return isFieldForCategory(currentCategories, fieldConfig);
  };

  // Resolve and validate all displayable custom fields
  const resolvedFields = {};
  (listingFieldConfigs || []).forEach(config => {
    if (config.showConfig?.displayOnListingPage === false) {
      return;
    }
    const isTargetCategory = isFieldForSelectedCategories(config);
    const isTargetListingType = isFieldForListingType(publicData?.listingType, config);
    if (!isTargetCategory || !isTargetListingType) {
      return;
    }

    const value =
      publicData?.[config.key] !== undefined ? publicData[config.key] : metadata?.[config.key];
    if (value === undefined || value === null) {
      return;
    }

    const displayVal = resolveFieldDisplayValue(config, value, intl);
    if (displayVal) {
      resolvedFields[config.key] = {
        label: config.showConfig?.label || config.key,
        value: displayVal,
      };
    }
  });

  const detailsList = [];

  // Order fields exactly as requested:
  // 1. Condition
  if (resolvedFields['condition']) {
    detailsList.push(resolvedFields['condition']);
  } else {
    const rawVal =
      publicData?.['condition'] !== undefined ? publicData['condition'] : metadata?.['condition'];
    if (rawVal !== undefined && rawVal !== null) {
      detailsList.push({
        label: intl.formatMessage({
          id: 'ListingPage.detailCondition',
          defaultMessage: 'Condition',
        }),
        value: String(rawVal),
      });
    }
  }

  // 2. Category (from hierarchy)
  if (categoryLabelValue) {
    detailsList.push({
      label: intl.formatMessage({ id: 'ListingPage.detailCategory', defaultMessage: 'Category' }),
      value: categoryLabelValue,
    });
  }

  // 3. Material
  if (resolvedFields['material']) {
    detailsList.push(resolvedFields['material']);
  } else {
    const rawVal =
      publicData?.['material'] !== undefined ? publicData['material'] : metadata?.['material'];
    if (rawVal !== undefined && rawVal !== null) {
      detailsList.push({
        label: intl.formatMessage({ id: 'ListingPage.detailMaterial', defaultMessage: 'Material' }),
        value: String(rawVal),
      });
    }
  }

  // 4. Dimensions
  if (resolvedFields['dimensions']) {
    detailsList.push(resolvedFields['dimensions']);
  } else {
    const rawVal =
      publicData?.['dimensions'] !== undefined
        ? publicData['dimensions']
        : metadata?.['dimensions'];
    if (rawVal !== undefined && rawVal !== null) {
      detailsList.push({
        label: intl.formatMessage({
          id: 'ListingPage.detailDimensions',
          defaultMessage: 'Dimensions',
        }),
        value: String(rawVal),
      });
    }
  }

  // 5. Brand
  if (resolvedFields['brand']) {
    detailsList.push(resolvedFields['brand']);
  } else {
    const rawVal = publicData?.['brand'] !== undefined ? publicData['brand'] : metadata?.['brand'];
    if (rawVal !== undefined && rawVal !== null) {
      detailsList.push({
        label: intl.formatMessage({ id: 'ListingPage.detailBrand', defaultMessage: 'Brand' }),
        value: String(rawVal),
      });
    }
  }

  // 6. Color
  if (resolvedFields['color']) {
    detailsList.push(resolvedFields['color']);
  } else {
    const rawVal = publicData?.['color'] !== undefined ? publicData['color'] : metadata?.['color'];
    if (rawVal !== undefined && rawVal !== null) {
      detailsList.push({
        label: intl.formatMessage({ id: 'ListingPage.detailColor', defaultMessage: 'Color' }),
        value: String(rawVal),
      });
    }
  }

  // 7. Any other configured custom fields (excluding standard keys)
  const standardKeys = ['condition', 'material', 'dimensions', 'brand', 'color'];
  Object.keys(resolvedFields).forEach(key => {
    if (!standardKeys.includes(key)) {
      detailsList.push(resolvedFields[key]);
    }
  });

  if (detailsList.length === 0) {
    return null;
  }

  return (
    <section className={css.productDetailsSection}>
      <Heading as="h2" rootClassName={css.productDetailsTitleHidden}>
        <FormattedMessage id="ListingPage.productDetailsTitle" defaultMessage="Product Details" />
      </Heading>
      <ul className={css.details}>
        {detailsList.map((item, idx) => (
          <li key={idx} className={css.detailsRow}>
            <span className={css.detailLabel}>{item.label}</span>
            <span className={css.detailValue}>{item.value}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

/**
 * AdCategoryBadge — shows only the category for advertisement listings.
 * Replaces the full ProductDetailsSection which is not relevant for ads.
 */
export const AdCategoryBadge = ({ publicData, categoryConfiguration }) => {
  if (!categoryConfiguration || !publicData) return null;

  const { key: prefix, categories } = categoryConfiguration;
  const labels = [];
  let currentOptions = categories;
  let level = 1;

  while (true) {
    const key = `${prefix}${level}`;
    const value = publicData?.[key];
    if (!value) break;
    const option = currentOptions?.find(o => o.id === value);
    if (!option) break;
    labels.push(option.name || option.label || value);
    currentOptions = option.subcategories || [];
    level++;
  }

  if (labels.length === 0) return null;

  return (
    <div className={css.adCategoryWrapper}>
      <span className={css.adCategoryLabel}>Category</span>
      <div className={css.adCategoryRow}>
        <svg className={css.adCategoryIcon} viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 1.99 2 1.99L16 19c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16z"/>
        </svg>
        <span className={css.adCategoryText}>{labels.join(' › ')}</span>
      </div>
    </div>
  );
};

/**
 * LocationSummary displaying City, State, and Distance (if available)
 */
export const LocationSummary = props => {
  const { publicData, distanceString } = props;
  const address = publicData?.location?.address;
  if (!address) {
    return null;
  }
  const parts = address.split(',').map(p => p.trim());
  let displayAddress = address;
  if (parts.length >= 2) {
    displayAddress = `${parts[0]}, ${parts[1]}`;
  }

  const LocationPinIcon = () => (
    <svg
      style={{ fill: "none" }}
      xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" /><circle cx="12" cy="10" r="3" /></svg>
  );

  return (
    <div className={css.locationSummary}>
      <LocationPinIcon />
      <span className={css.locationText}>
        {displayAddress} {distanceString ? `• ${distanceString}` : ''}
      </span>
    </div>
  );
};

const CONTACT_USER_LINK = 'inquiryModalContactUserLink';

/**
 * SellerCard displaying Avatar, Display Name, Member Since, Ratings Summary,
 * and integrated message flow / favorites buttons.
 */

/**
 * SellerInfoBlock displaying Avatar, Display Name, Member Since, and Review/Ratings Summary.
 */
export const SellerInfoBlock = props => {
  const { ensuredAuthor, authorDisplayName, reviews } = props;

  if (!ensuredAuthor) {
    return null;
  }

  const memberSinceYear = ensuredAuthor.attributes.createdAt
    ? new Date(ensuredAuthor.attributes.createdAt).getFullYear()
    : null;

  const totalReviewsCount = reviews?.length || 0;
  const ratingSum = reviews?.reduce((sum, r) => sum + r.attributes.rating, 0) || 0;
  const averageRating = totalReviewsCount > 0 ? (ratingSum / totalReviewsCount).toFixed(1) : null;

  const displayRating = totalReviewsCount > 0 ? Math.round(Number(averageRating)) : 0;

  return (
    <div className={css.sellerInfoBlock}>
      <AvatarLarge user={ensuredAuthor} className={css.sellerAvatar} />
      <div className={css.sellerInfo}>
        <div className={css.sellerName}>{authorDisplayName}</div>
        {memberSinceYear && (
          <div className={css.sellerMemberSince}>
            <FormattedMessage id="ListingPage.memberSince" values={{ year: memberSinceYear }} />
          </div>
        )}
        <div className={css.sellerRatingContainer}>
          <ReviewRating
            rating={displayRating}
            className={css.sellerStars}
            reviewStarClassName={css.sellerStarIcon}
          />
          <span className={css.sellerRatingText}>({totalReviewsCount})</span>
        </div>
      </div>
    </div>
  );
};

/**
 * SellerButtons displaying Message Seller, Add to Favorites, and the inquiry Modal.
 */
export const SellerButtons = props => {
  const {
    title,
    ensuredAuthor,
    authorDisplayName,
    onContactUser,
    isInquiryModalOpen,
    onCloseInquiryModal,
    sendInquiryError,
    sendInquiryInProgress,
    onSubmitInquiry,
    onManageDisableScrolling,
    listing,
  } = props;

  if (!ensuredAuthor) {
    return null;
  }

  const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
  const unitType = listing?.attributes?.publicData?.unitType || '';
  const isInquiryProcess = isInquiryProcessAlias(transactionProcessAlias);
  const isNegotiationProcess = isNegotiationProcessAlias(transactionProcessAlias);
  const showContact = !(isInquiryProcess || (isNegotiationProcess && unitType === OFFER));

  const handleContactClick = () => {
    onContactUser(ensuredAuthor);
  };

  const handleAddToFavorites = () => {
    // Integration point for Add to Favorites functionality
    console.log('Add to favorites clicked for listing ID:', listing?.id?.uuid);
  };

  const ChatIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginRight: '10px', display: 'inline-block', verticalAlign: 'middle' }}
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    </svg>
  );

  const HeartIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginRight: '10px', display: 'inline-block', verticalAlign: 'middle' }}
    >
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
    </svg>
  );

  return (
    <React.Fragment>
      <div className={css.sellerCardActions}>
        {showContact && (
          <PrimaryButton onClick={handleContactClick} className={css.messageButton}>
            <ChatIcon />
            <FormattedMessage id="ListingPage.messageSeller" defaultMessage="Message Seller" />
          </PrimaryButton>
        )}
        <SecondaryButton onClick={handleAddToFavorites} className={css.favoritesButton}>
          <HeartIcon />
          <FormattedMessage id="ListingPage.addToFavorites" defaultMessage="Add to Favorites" />
        </SecondaryButton>
      </div>

      <Modal
        id="ListingPage.inquiry"
        contentClassName={css.inquiryModalContent}
        isOpen={isInquiryModalOpen}
        onClose={onCloseInquiryModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={CONTACT_USER_LINK}
      >
        <InquiryForm
          className={css.inquiryForm}
          submitButtonWrapperClassName={css.inquirySubmitButtonWrapper}
          listingTitle={title}
          authorDisplayName={authorDisplayName}
          sendInquiryError={sendInquiryError}
          onSubmit={onSubmitInquiry}
          inProgress={sendInquiryInProgress}
        />
      </Modal>
    </React.Fragment>
  );
};

/**
 * SectionAdvertisementContact — displays contact details for advertisement listings.
 * Reads website, phone, contactEmail, and social links from publicData.
 * The business name is already shown as the listing title/heading on the page.
 */
export const SectionAdvertisementContact = ({ publicData }) => {
  if (!publicData) return null;

  const {
    website, phone, contactEmail,
    socialFacebook, socialInstagram, socialTwitter, socialLinkedin, socialYoutube,
  } = publicData;

  const hasAny = website || phone || contactEmail ||
    socialFacebook || socialInstagram || socialTwitter || socialLinkedin || socialYoutube;
  if (!hasAny) return null;

  const safeHref = url => (url && url.startsWith('http') ? url : `https://${url}`);

  const ContactRow = ({ icon, label, href, text }) => (
    <div className={css.adContactRow}>
      <span className={css.adContactIcon}>{icon}</span>
      <div className={css.adContactContent}>
        <span className={css.adContactLabel}>{label}</span>
        {href ? (
          <a className={css.adContactLink} href={href} target="_blank" rel="noopener noreferrer">
            {text}
          </a>
        ) : (
          <span className={css.adContactText}>{text}</span>
        )}
      </div>
    </div>
  );

  return (
    <div className={css.adContactSection}>
      <h3 className={css.adContactTitle}>
        <FormattedMessage id="ListingPage.advertisementContactTitle" defaultMessage="Contact & Links" />
      </h3>

      {website && (
        <ContactRow
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>}
          label="Website"
          href={safeHref(website)}
          text={website}
        />
      )}
      {phone && (
        <ContactRow
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.1 6.1l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>}
          label="Phone"
          href={`tel:${phone}`}
          text={phone}
        />
      )}
      {contactEmail && (
        <ContactRow
          icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>}
          label="Email"
          href={`mailto:${contactEmail}`}
          text={contactEmail}
        />
      )}

      {(socialFacebook || socialInstagram || socialTwitter || socialLinkedin || socialYoutube) && (
        <div className={css.adSocialRow}>
          <span className={css.adContactLabel}>Follow us</span>
          <div className={css.adSocialLinks}>
            {socialFacebook && (
              <a href={safeHref(socialFacebook)} target="_blank" rel="noopener noreferrer" className={css.adSocialLink} title="Facebook">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.04c-5.5 0-10 4.49-10 10.02 0 5 3.66 9.15 8.44 9.9v-7H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.23.19 2.23.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.45 2.9h-2.33v7a10 10 0 0 0 8.44-9.9c0-5.53-4.5-10.02-10-10.02z"/></svg>
              </a>
            )}
            {socialInstagram && (
              <a href={safeHref(socialInstagram)} target="_blank" rel="noopener noreferrer" className={css.adSocialLink} title="Instagram">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
            )}
            {socialTwitter && (
              <a href={safeHref(socialTwitter)} target="_blank" rel="noopener noreferrer" className={css.adSocialLink} title="Twitter / X">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L2.02 2.25H8.5l4.243 5.608L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            )}
            {socialLinkedin && (
              <a href={safeHref(socialLinkedin)} target="_blank" rel="noopener noreferrer" className={css.adSocialLink} title="LinkedIn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>
              </a>
            )}
            {socialYoutube && (
              <a href={safeHref(socialYoutube)} target="_blank" rel="noopener noreferrer" className={css.adSocialLink} title="YouTube">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.4a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white"/></svg>
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
